import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { budgetsAPI, departmentsAPI, financialYearsAPI } from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import { Plus, Edit2, Trash2, Search, Calendar, Layers, CalendarRange, ChevronUp, ChevronDown } from 'lucide-react';
import TablePagination from '../components/TablePagination';

const BUDGET_PAGE_SIZE = 25;

export default function BudgetHeaders() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [budgets, setBudgets] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [budgetPage, setBudgetPage] = useState(1);

  const [formData, setFormData] = useState({
    budgetName: '',
    budgetType: 'Departmental',
    departmentId: '',
    financialYearId: '',
    totalBudget: '',
    status: 'Draft'
  });

  const canManage = hasPermission('manage_budgets');

  useEffect(() => {
    fetchBudgets();
    fetchDepartments();
    fetchFinancialYears();
  }, [filterYear]);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterYear) params.fiscalYear = filterYear;
      const response = await budgetsAPI.getAll(params);
      setBudgets(response.data.items || []);
    } catch (err) {
      console.error('Error fetching budgets:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await departmentsAPI.getAll();
      const depts = response.data.departments || response.data.items || response.data || [];
      setDepartments(Array.isArray(depts) ? depts : []);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setDepartments([]);
    }
  };

  const fetchFinancialYears = async () => {
    try {
      const response = await financialYearsAPI.getAll();
      const years = response.data.items || response.data.financialYears || response.data || [];
      setFinancialYears(Array.isArray(years) ? years : []);
    } catch (err) {
      console.error('Error fetching financial years:', err);
      setFinancialYears([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBudget) {
        await budgetsAPI.update(editingBudget.BudgetID, formData);
      } else {
        await budgetsAPI.create(formData);
      }
      setShowModal(false);
      resetForm();
      fetchBudgets();
    } catch (err) {
      console.error('Error saving budget:', err);
      alert(err.response?.data?.message || 'Failed to save budget');
    }
  };

  const handleDelete = async (budgetId) => {
    if (!window.confirm('Are you sure you want to delete this budget header?')) return;
    
    try {
      await budgetsAPI.delete(budgetId);
      fetchBudgets();
    } catch (err) {
      console.error('Error deleting budget:', err);
      alert('Failed to delete budget');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setEditingBudget(null);
    fetchDepartments();
    fetchFinancialYears();
    setShowModal(true);
  };

  const openEditModal = (budget) => {
    setEditingBudget(budget);
    fetchDepartments();
    fetchFinancialYears();
    setFormData({
      budgetName: budget.BudgetName || '',
      budgetType: budget.BudgetType || 'Departmental',
      departmentId: budget.DepartmentID || '',
      financialYearId: budget.FinancialYearID || '',
      totalBudget: budget.TotalBudget || '',
      status: budget.Status || 'Draft'
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      budgetName: '',
      budgetType: 'Departmental',
      departmentId: '',
      financialYearId: '',
      totalBudget: '',
      status: 'Draft'
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-600" /> : <ChevronDown className="w-3 h-3 text-blue-600" />;
  };

  const filteredBudgets = budgets.filter(budget =>
    budget.BudgetName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    budget.DepartmentName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedBudgets = [...filteredBudgets].sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    const numericFields = ['TotalBudget', 'TotalAllocated', 'TotalRemaining'];
    if (numericFields.includes(sortField)) {
      return ((a[sortField] || 0) - (b[sortField] || 0)) * dir;
    }
    return (a[sortField] || '').toString().localeCompare((b[sortField] || '').toString()) * dir;
  });
  const pagedBudgets = sortedBudgets.slice((budgetPage - 1) * BUDGET_PAGE_SIZE, budgetPage * BUDGET_PAGE_SIZE);
  // Reset page on filter change
  React.useEffect(() => { setBudgetPage(1); }, [searchTerm, filterYear]);

  const getStatusBadge = (status) => {
    const styles = {
      Draft: 'bg-gray-100 text-gray-800',
      Active: 'bg-green-100 text-green-800',
      Closed: 'bg-red-100 text-red-800'
    };
    return styles[status] || styles.Draft;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Budget Headers</h1>
          <p className="text-gray-600 mt-1">Manage department and project budget allocations</p>
        </div>
        {canManage && (
          <button onClick={openCreateModal} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Create Budget Header
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search budgets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="w-56">
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Financial Years</option>
              {financialYears.map(fy => (
                <option key={fy.FinancialYearID} value={fy.FinancialYear}>
                  {fy.YearLabel || fy.FinancialYear}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Budgets Table */}
      <div className="card">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('BudgetName')}><div className="flex items-center gap-1">Budget Name<SortIcon field="BudgetName" /></div></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('BudgetType')}><div className="flex items-center gap-1">Type<SortIcon field="BudgetType" /></div></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('DepartmentName')}><div className="flex items-center gap-1">Department<SortIcon field="DepartmentName" /></div></th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('FinancialYear')}><div className="flex items-center justify-center gap-1">Financial Year<SortIcon field="FinancialYear" /></div></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('TotalBudget')}><div className="flex items-center justify-end gap-1">Total Budget<SortIcon field="TotalBudget" /></div></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('TotalAllocated')}><div className="flex items-center justify-end gap-1">Allocated<SortIcon field="TotalAllocated" /></div></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('TotalRemaining')}><div className="flex items-center justify-end gap-1">Remaining<SortIcon field="TotalRemaining" /></div></th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('Status')}><div className="flex items-center justify-center gap-1">Status<SortIcon field="Status" /></div></th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Lines</th>
                  {canManage && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pagedBudgets.map((budget) => (
                  <tr key={budget.BudgetID} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                      {budget.BudgetName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {budget.BudgetType || 'Departmental'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {budget.DepartmentName || 'Company-wide'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center text-gray-600">
                      <div className="flex items-center justify-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {budget.YearLabel || budget.FinancialYear || 'N/A'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-gray-900 font-medium">
                      £{(budget.TotalBudget || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600">
                      £{(budget.TotalAllocated || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-gray-900">
                      £{(budget.TotalRemaining || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(budget.Status)}`}>
                        {budget.Status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <button
                        onClick={() => navigate(`/budget-lines?budgetId=${budget.BudgetID}`)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
                        title="View Budget Lines"
                      >
                        <Layers className="w-4 h-4" />
                        View Lines
                      </button>
                      <button
                        onClick={() => navigate(`/budget-monthly-view?budgetId=${budget.BudgetID}`)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 rounded transition-colors"
                        title="Monthly Budget Spread"
                      >
                        <CalendarRange className="w-4 h-4" />
                        Monthly View
                      </button>
                    </td>
                    {canManage && (
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openEditModal(budget)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(budget.BudgetID)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {sortedBudgets.length === 0 && (
                  <tr>
                    <td colSpan={canManage ? 8 : 7} className="px-4 py-8 text-center text-gray-500">
                      No budget headers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <TablePagination page={budgetPage} pageSize={BUDGET_PAGE_SIZE} total={sortedBudgets.length} onPageChange={setBudgetPage} />
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingBudget ? 'Edit Budget Header' : 'Create Budget Header'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.budgetName}
                  onChange={(e) => setFormData({ ...formData, budgetName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Q1 2025 Budget"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget Type *
                </label>
                <select
                  required
                  value={formData.budgetType}
                  onChange={(e) => setFormData({ ...formData, budgetType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Departmental">Departmental Budget</option>
                  <option value="Business Global">Business Global Budget</option>
                  <option value="Project">Project Budget</option>
                  <option value="Programme">Programme Budget</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department (Optional)
                </label>
                <select
                  value={formData.departmentId}
                  onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Company-wide</option>
                  {departments.map(dept => (
                    <option key={dept.DepartmentID} value={dept.DepartmentID}>
                      {dept.DepartmentName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Financial Year *
                </label>
                <select
                  required
                  value={formData.financialYearId}
                  onChange={(e) => setFormData({ ...formData, financialYearId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select financial year...</option>
                  {financialYears.map(fy => (
                    <option key={fy.FinancialYearID} value={fy.FinancialYearID}>
                      {fy.YearLabel} ({fy.FinancialYear})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Budget (£) *
                </label>
                <input
                  type="number"
                  required
                  step="0.01"
                  min="0"
                  value={formData.totalBudget}
                  onChange={(e) => setFormData({ ...formData, totalBudget: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status *
                </label>
                <select
                  required
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Draft">Draft</option>
                  <option value="Active">Active</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1">
                  {editingBudget ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
